import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, PlusCircle, Upload, Library, Bot,
  Shield, ShieldCheck, BookOpen, GraduationCap,
  Users, Settings,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CommandBar } from "@/components/CommandBar";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const quickActions = [
  {
    label: "Create Policy",
    description: "Draft a new policy",
    icon: PlusCircle,
    href: "/begin",
    color: "text-violet-600",
    bg: "bg-violet-50 dark:bg-violet-950/40",
  },
  {
    label: "Upload Policy",
    description: "Import PDF or Word doc",
    icon: Upload,
    href: "/upload-policy",
    color: "text-blue-600",
    bg: "bg-blue-50 dark:bg-blue-950/40",
  },
  {
    label: "Policy Library",
    description: "Browse all policies",
    icon: Library,
    href: "/policy-library",
    color: "text-indigo-600",
    bg: "bg-indigo-50 dark:bg-indigo-950/40",
  },
  {
    label: "AI Agent",
    description: "Chat with Polybot",
    icon: Bot,
    href: "/ai-agent",
    color: "text-purple-600",
    bg: "bg-purple-50 dark:bg-purple-950/40",
  },
  {
    label: "Compliance",
    description: "Monitor compliance",
    icon: Shield,
    href: "/compliance",
    color: "text-emerald-600",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
  },
  {
    label: "Risk Overview",
    description: "Review risk posture",
    icon: ShieldCheck,
    href: "/risk/overview",
    color: "text-orange-600",
    bg: "bg-orange-50 dark:bg-orange-950/40",
  },
  {
    label: "Knowledge Base",
    description: "Company documents",
    icon: BookOpen,
    href: "/knowledge-base",
    color: "text-cyan-600",
    bg: "bg-cyan-50 dark:bg-cyan-950/40",
  },
  {
    label: "Quizzes",
    description: "Test policy knowledge",
    icon: GraduationCap,
    href: "/quizzes",
    color: "text-pink-600",
    bg: "bg-pink-50 dark:bg-pink-950/40",
  },
  {
    label: "Team",
    description: "Manage members",
    icon: Users,
    href: "/team",
    color: "text-amber-600",
    bg: "bg-amber-50 dark:bg-amber-950/40",
  },
  {
    label: "Settings",
    description: "Account & preferences",
    icon: Settings,
    href: "/settings",
    color: "text-zinc-600",
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0.2, 0, 0, 1] } },
};

export default function Home() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [commandOpen, setCommandOpen] = useState(false);

  const firstName = profile?.name?.split(" ")[0] ?? "there";

  return (
    <>
      <CommandBar open={commandOpen} onClose={() => setCommandOpen(false)} />

      <div className="flex flex-col items-center justify-start min-h-[calc(100vh-10rem)] pt-16 pb-12 px-4">
        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.2, 0, 0, 1] }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-medium text-foreground mb-2">
            {getGreeting()}, {firstName}.
          </h1>
          <p className="text-muted-foreground text-lg">
            What would you like to do today?
          </p>
        </motion.div>

        {/* Search bar */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.08, ease: [0.2, 0, 0, 1] }}
          className="w-full max-w-xl mb-12"
        >
          <button
            onClick={() => setCommandOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-border bg-background shadow-sm hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] text-left group"
          >
            <Search className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            <span className="flex-1 text-sm text-muted-foreground">
              Search policies, actions, pages…
            </span>
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground bg-muted">
              <span className="text-xs">⌘</span>K
            </kbd>
          </button>
        </motion.div>

        {/* Quick actions */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="w-full max-w-3xl"
        >
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4 text-center">
            Quick Access
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <motion.button
                  key={action.href}
                  variants={item}
                  onClick={() => navigate(action.href)}
                  className="flex flex-col items-center gap-2.5 p-4 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-[border-color,box-shadow,transform] active:scale-[0.96] text-center group"
                >
                  <div
                    className={`h-10 w-10 rounded-xl flex items-center justify-center ${action.bg} ${action.color} transition-transform group-hover:scale-110`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground leading-tight">
                      {action.label}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                      {action.description}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </>
  );
}
